import type { GarantiaConfiguracao } from '@pgcustom/shared-types';
import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { GarantiasApi } from './garantias.api.js';

/**
 * `<erp-configuracao-garantia-modal>` — define `diasCobertura`
 * (`garantias.configuracao`, migration `015`). Nenhum documento do
 * projeto especifica um número fixo de dias — por isso este modal é o
 * único lugar do sistema que grava esse valor, sempre uma escolha
 * explícita do admin, nunca um padrão inventado no código. Mesmo padrão
 * de `<erp-movimentacao-form-modal>`.
 */
export class ErpConfiguracaoGarantiaModal extends HTMLElement {
  private readonly root: ShadowRoot;
  private _garantiasApi: GarantiasApi | null = null;
  private _aoSalvar: ((configuracao: GarantiaConfiguracao) => void) | null = null;

  private aberto = false;
  private diasAtuais: number | null = null;
  private enviando = false;
  private mensagemErro = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set garantiasApi(valor: GarantiasApi) {
    this._garantiasApi = valor;
  }

  set aoSalvar(valor: (configuracao: GarantiaConfiguracao) => void) {
    this._aoSalvar = valor;
  }

  abrir(diasAtuais: number | null): void {
    this.diasAtuais = diasAtuais;
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  connectedCallback(): void {
    this.render();
  }

  private fechar(): void {
    if (this.enviando) return;
    this.aberto = false;
    this.render();
  }

  private async aoSubmeter(evento: Event): Promise<void> {
    evento.preventDefault();
    if (!this._garantiasApi || this.enviando) return;

    const diasCobertura = Number(this.valorCampo('#dias-cobertura'));
    if (!Number.isInteger(diasCobertura) || diasCobertura <= 0) {
      this.mensagemErro = 'dias de cobertura deve ser um número inteiro maior que zero';
      this.render();
      return;
    }

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      const configuracao = await this._garantiasApi.definirConfiguracao(diasCobertura);
      this.aberto = false;
      this._aoSalvar?.(configuracao);
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível salvar. tente novamente.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private valorCampo(seletor: string): string {
    return this.root.querySelector<HTMLInputElement>(seletor)?.value ?? '';
  }

  private render(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; }
        form { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); font-family: var(--pg-font-familia-base, system-ui); }
        ${FORM_FIELD_CSS}
        .ajuda { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-desabilitado, #5c626b); }
        .rodape { display: flex; justify-content: flex-end; gap: var(--pg-space-3, 12px); }
      </style>
      <pg-modal id="modal" ${this.aberto ? 'open' : ''}>
        <span slot="header">Configurar cobertura de garantia</span>
        <form novalidate>
          <label>
            Dias de cobertura
            <input id="dias-cobertura" type="number" min="1" step="1" value="${this.diasAtuais ?? ''}" />
          </label>
          <span class="ajuda">Prazo de garantia contado a partir da data de entrega confirmada de cada envio.</span>
          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
        </form>
        <div slot="footer" class="rodape">
          <pg-button id="botao-cancelar" variant="ghost">Cancelar</pg-button>
          <pg-button id="botao-salvar" variant="primary" ${this.enviando ? 'loading' : ''}>Salvar</pg-button>
        </div>
      </pg-modal>
    `;

    const form = this.root.querySelector('form');
    form?.addEventListener('submit', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-salvar')?.addEventListener('pg-click', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-cancelar')?.addEventListener('pg-click', () => this.fechar());
    this.root.querySelector('#modal')?.addEventListener('pg-close', () => this.fechar());
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-configuracao-garantia-modal', ErpConfiguracaoGarantiaModal);

declare global {
  interface HTMLElementTagNameMap {
    'erp-configuracao-garantia-modal': ErpConfiguracaoGarantiaModal;
  }
}
